import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Spinner } from "@radix-ui/themes";
import { debounce, isEmpty } from "lodash";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DateFieldComponent from "../../../components/inputs/Datefield/DatefieldComponent";
import RadioGroupComponent from "../../../components/inputs/RadioGroup/RadioGroupComponent";
import MultiSelectComponent from "../../../components/inputs/Select/MultiSelectComponent";
import SelectComponent from "../../../components/inputs/Select/SelectComponent";
import TextAreaFieldComponent from "../../../components/inputs/TextAreafield/TextAreafieldComponent";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../../hooks/hooks";
import { useGetPaymentTypesQuery } from "../../../hooks/useGetPaymentTypesQuery";
import { useGetSchoolBankConfigQuery } from "../../../hooks/useGetSchoolBankConfigQuery";
import { setStudentDebtData } from "../../../redux/slices/finance/studentDebt";
import { setPdfPreviewType } from "../../../redux/slices/report/report";
import { resetStudentData, setStudentData } from "../../../redux/slices/school/student";
import { resetUserData, setUserData } from "../../../redux/slices/users/user";
import { RootState } from "../../../redux/store";
import { routes } from "../../../routes";
import { createPaymentReference, getReferenceCodeV2 } from "../../../services/finance/paymentReference";
import { createStudentDebt, getStudentDebt } from "../../../services/finance/studentDebt";
import { getStudentByCode } from "../../../services/school/student/getStudentByCode";
import { getUserById } from "../../../services/user/getUsers";
import { PaymentReferenceType, StudentDebtType } from "../../../types/Finance";
import { months, monthsTranslation } from "../../../utils/months";
import { stringToBoolean } from "../../../utils/utilities";
import { UserPreview } from "../../Users/UserPreview";
import { CreateStudentDebtFormValidation } from "./CreateStudentDebtFormValidation";
import { printInvoice, SchoolBankConfig } from "./printInvoice";
import { useExportDocument } from "../../../hooks/useExportDocument";
import { useIES } from "../../../hooks/useIES";
import { useLogoBase64 } from "../../../hooks/useLogoBase64";

export const CreateStudentDebt = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { IES } = useIES();
  const { logoBase64 } = useLogoBase64();
  const loggedUserData = useSelector(
      (state: RootState) => state.login.userData
    );
  
  const mutation = useMutation({ mutationFn: createStudentDebt, mutationKey: 'createStudentDebt' });
  const { data: paymentTypesData } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 4000 });
  const { data: schoolBankConfigsData, status: schoolBankConfigsStatus, isError: isErrorSchoolschoolBankConfigs, isLoading: isLoadingSchoolschoolBankConfigs } = useGetSchoolBankConfigQuery({ PageNumber: 1, PageSize: 400 });
  const schoolBankConfigsArray: SchoolBankConfig[] = schoolBankConfigsData?.items.map(i => ({
  bankEntity: i.bankEntity!,
  bankName: i.bankName!
})) ?? [];
  const mutationSaveReferenceCode = useMutation({ mutationFn: createPaymentReference, mutationKey: 'createPaymentReference' });
const { exportDocumentPdf } = useExportDocument();
  const userData = useSelector((state: RootState) => state.user);
  const defaultValues: StudentDebtType = {
    idStudent: "",
    idPaymentType: "",
    originalAmount: 0,
    currentAmount: 0,
    dueDate: "",
    isPayed: false,
    isMultipleMonths: false,
    paidDate: null,
    description: "",
    generateRef: false,
    bankEntity: '',
  }
  const { control, handleSubmit, formState: { errors }, setValue, getValues, watch } = useForm<StudentDebtType>
    (
      {
        resolver: yupResolver(CreateStudentDebtFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
      }
    );
  const handleGenerateReference = async (c: StudentDebtType) => {
    if (c.isPayed) {
      toast.error("Pago, não será possivel gerar referencia");
      return;
    }
    try {
      const bankEntity = getValues('bankEntity');
      if (isEmpty(bankEntity)) {
        toast.error("Selecione uma entidade bancaria");
        return;
      }
      const currentDate = new Date();
      let referenceCode = '';
      try {
        referenceCode = await getReferenceCodeV2({
          amount: c.currentAmount,
          idStudent: c.idStudent,
          idPaymentType: c.idPaymentType,
          idStudentDebt: c.id,
          month: (currentDate.getMonth() + 1).toString(),
          year: currentDate.getFullYear().toString(),
          description: c.description,
          idBankEntity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('bankEntity'))?.id),
          entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('bankEntity'))?.bankEntity)
        });
      } catch (ex: any) {
        toast.error(ex?.message);
        return;
      }
      if (isEmpty(referenceCode)) {
        toast.error("Ops, não foi possivel gerar referencia");
        return;
      }
      const data: PaymentReferenceType = {
        idStudent: c.idStudent,
        idPaymentType: c.idPaymentType,
        amount: c.currentAmount,
        dueDate: new Date(c.dueDate).toISOString(),
        referenceCode: referenceCode,
        idStudentDebt: c.id,
        entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('bankEntity'))?.bankEntity),
        idBankEntity: bankEntity
      }
      await mutationSaveReferenceCode.mutateAsync(data)
      return referenceCode;
      // reset();
    } catch (ex) {
      toast.error("Ops, ocorreu um erro!");
    }
  };
  const onSubmit = handleSubmit(async (data) => {
    data.isMultipleMonths = stringToBoolean(data.isMultipleMonths);
    try {
      try {
        data.generateRef = stringToBoolean(data.generateRef);
      } catch (error) {
        console.error({ error })
      }
      const result = await mutation.mutateAsync(data);
      if (result) {
        data.id = result;
        
        let studentDebtData = { ...data };
        studentDebtData.paymentType = paymentTypesData?.items?.find(i => i.id === data.idPaymentType)?.description;
        try {
          const studentDebtDataRequest = await getStudentDebt({ PageSize: 1, PageNumber: 1, Id: data.id });
          const studentDebtFromServer = studentDebtDataRequest?.items?.at(0);
          studentDebtData = { ...data, ...studentDebtFromServer }
        } catch (ex) {
          console.error(ex);
        }
        if (data.generateRef) {
          try {
            const referenceCode = await handleGenerateReference(data);
            data.referenceCode = referenceCode;
            toast.success("Criada");
            try {
              if (isEmpty(studentDebtData.referenceCode)) {
                studentDebtData.referenceCode = referenceCode;
              }
              const schoolBankConfig = schoolBankConfigsData?.items.find(i => i.id === getValues('bankEntity'));
              studentDebtData.bankName = schoolBankConfig?.bankName;
              studentDebtData.bankDescription = schoolBankConfig?.bankDescription;
              studentDebtData.entityCode = String(schoolBankConfig?.bankEntity);
            } catch (error) {
              console.log(error);
            }
             dispatch(setStudentDebtData(studentDebtData))
            await printInvoice({
              studentDebtId: data.id!,
              schoolBankConfigsData: schoolBankConfigsArray,
              IES,
              logoBase64: logoBase64 ?? undefined,
              exportDocument: exportDocumentPdf,
               loggedUserData: loggedUserData,
              dispatch,
            });
             navigate(routes.studentDebts);
          } catch (error) {
            toast.error(" Ops Sem referencia!");
            navigate(routes.studentDebts);
          }
        } else {
           dispatch(setStudentDebtData(data))
           await printInvoice({
              studentDebtId: data.id!,
              schoolBankConfigsData: schoolBankConfigsArray,
              IES,
              logoBase64: logoBase64 ?? undefined,
              exportDocument: exportDocumentPdf,
               loggedUserData: loggedUserData,
              dispatch,
            });
          // toast.success("Criada!");
          navigate(routes.studentDebts);
        }
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handleBack = () => {
    navigate(routes.studentDebts);
    dispatch(resetStudentData());
    dispatch(resetUserData());
  }
  const { isLoading: isLoadingGetStudent, refetch: fetchStudentByCode, isError: isErrorGetStudent, error } = useQuery(
    ['getStudentByCode'],
    async () => {
      const studentCodeValue = getValues('studentCode');

      // if (studentCodeValue.trim().length < 17) return;
      try {
        const studentData = await getStudentByCode(studentCodeValue);
        if (!studentData) return;
        const userData = await getUserById(studentData.idUser)
        if (!userData) return;
        dispatch(setStudentData(studentData));
        dispatch(setUserData(userData));
        setValue('idStudent', studentData.id!);
        setValue('fullname', userData.name);
        if (studentCodeValue) {
          //  fetchStudentDebts();
        }

      } catch (error) {
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const handleStudentCodeChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue('studentCode', trimmedValue);
      if (trimmedValue.length >= 5) {
        fetchStudentByCode();
      } else {
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    }, 200),
    [setValue, fetchStudentByCode]
  );
  const handleSelectPaymentType = (idPayment: string) => {
    const paymentType = paymentTypesData?.items.find(i => i.id === idPayment);
    if (paymentType) {
      setValue('currentAmount', paymentType.amount)
      setValue('originalAmount', paymentType.amount)
      const isMultipleMonths = getValues('isMultipleMonths');
      if (stringToBoolean(isMultipleMonths)) {
        let months = getValues('months');
        if (!isEmpty(months)) {
          let monthsArray = months?.split(',');
          handleMonthsChange(monthsArray!);
        }
      }
    }
  }
  const watchGenerateRef = watch('generateRef')
  const watchIsMultipleMonths = watch('isMultipleMonths')
  function handleMonthsChange(value: string[]): void {
    // const isMultipleMonths =getValues('isMultipleMonths');
    // if(stringToBoolean(isMultipleMonths)){
    //   setValue('month', '');
    // }
    setValue('months', value.join(','));
    const countMonths = value.length;
    const currentAmount = paymentTypesData?.items.find(i => i.id === getValues('idPaymentType'))?.amount ?? 0;
    const newAmount = countMonths * currentAmount;
    setValue('currentAmount', newAmount);
    setValue('originalAmount', newAmount);
  }
  const handleChangeIsMultipleMonths = (e: any) => {
    const isMultipleMonthsString = getValues('isMultipleMonths');
    const isMultipleMonths = stringToBoolean(isMultipleMonthsString);
    if (isMultipleMonths) {
      setValue('month', '');
    }
  };
  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>
          <TextFieldComponent
            name="studentCode"
            label="Código do estudante"
            control={control}
            errors={errors}
            fieldProps={{
              placeholder: 'Digite o código do estudante',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleStudentCodeChange(e.target.value),
              onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData('text');
                handleStudentCodeChange(pastedText);
              }
            }}
            required={true}
          />
          {userData && userData?.id && <>
            <UserPreview />
          </>}
          <SelectComponent name="idPaymentType" label="Tipo de emolumento" control={control} errors={errors} style={{ width: '100%' }}
            options={paymentTypesData?.items.map((paymentType) => ({
              value: paymentType?.id ?? "",
              label: paymentType?.description ?? ""
            }))}
            onSelect={handleSelectPaymentType}
            required={true}
          />
          <TextFieldComponent name="originalAmount" label="Valor a pagar" control={control} errors={errors} fieldProps={{ placeholder: 'Valor a pagar' }} />
          <TextFieldComponent name="currentAmount" label="Valor a pagar remanescente" control={control} errors={errors} fieldProps={{ placeholder: 'Valor a pagar remanescente' }} />
          <RadioGroupComponent name="isMultipleMonths" label="Multiplos Meses" control={control} errors={errors}
            style={{ width: '50%' }}
            options={[
              { value: "true", label: "Sim" }
              , { value: "false", label: "Não" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: '16px' },
              onChange: handleChangeIsMultipleMonths
            }}
          />
          {stringToBoolean(watchIsMultipleMonths) ? <>
            <MultiSelectComponent
              label="Meses a cobrar"
              options={months.map((month) => ({
                value: month,
                label: monthsTranslation[month as keyof typeof monthsTranslation] || month
              }))}
              errors={errors}
              control={control}
              name={"months"}
              onSelect={handleMonthsChange}
            />
          </> :
            <SelectComponent
              label="Mês a cobrar"
              options={months.map((month) => ({
                value: month,
                label: monthsTranslation[month as keyof typeof monthsTranslation] || month
              }))}
              errors={errors}
              control={control}
              name={"month"}
            />
          }
          <DateFieldComponent name="dueDate" label="Data de vencimento" control={control} errors={errors} style={{ width: '100%' }} required={true} />
          <RadioGroupComponent name="generateRef" label="Gerar ref." control={control} errors={errors}
            style={{ width: '50%' }}
            options={[
              { value: "true", label: "Sim" }
              , { value: "false", label: "Não" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: '16px' },
            }}
          />
          <SelectComponent
            name="bankEntity"
            label="Entidade Bancária"
            control={control}
            errors={errors}
            itemsLoading={isLoadingSchoolschoolBankConfigs}
            itemsError={isErrorSchoolschoolBankConfigs}
            options={schoolBankConfigsData?.items?.map(item => ({
              value: item.id!,
              label: `${String(item.bankEntity!)} - ${item?.bankName ?? ''} (${item?.bankDescription ?? ''})`,
            })) ?? []}
            style={{ width: '100%' }}
            fieldProps={{ disabled: !stringToBoolean(watchGenerateRef) }}
            required={stringToBoolean(watchGenerateRef)}
          />
          <TextAreaFieldComponent
            name="description"
            label="Descrição"
            control={control}
            errors={errors}
            fieldProps={{ placeholder: 'Descrição' }}
            style={{ width: '100%' }}
          />

          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'  >
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}
